import type { TripStatus, TripStopStatus } from "../../entities/trip";

// Libellés FR + tons pour affichage UI uniquement.
// ⚠️ TripStatus ≠ OrderStatus (le Trip a IN_PROGRESS au lieu de
// PURCHASING/PURCHASED) — ne PAS réutiliser orderStatusLabel ici.
//
// Le ton réutilise la palette du StatusBadge : success | warning | danger | neutral.

export type StatusTone = "success" | "warning" | "danger" | "neutral";

// ─── Statut de la course (Trip) ──────────────────────────────────────────────

const TRIP_LABELS: Record<TripStatus, string> = {
  PENDING_WALLET_CHECK:     "Vérification du wallet…",
  WALLET_FUNDS_BLOCKED:     "Fonds bloqués",
  AGENT_OFFER_PENDING:      "Recherche d'une Main…",
  AGENT_ASSIGNED:           "Main attribuée",
  AGENT_EN_ROUTE:           "En route",
  IN_PROGRESS:              "Course en cours",
  AWAITING_CLIENT_APPROVAL: "Validation requise",
  APPROVAL_EXPIRED:         "Délai de validation dépassé",
  DELIVERING:               "Livraison en cours",
  DELIVERED:                "Livré",
  PAYMENT_SETTLED:          "Paiement finalisé",
  CANCELLED:                "Annulée",
  DISPUTED:                 "En litige",
  REFUNDED:                 "Remboursée",
};

const TRIP_TONES: Record<TripStatus, StatusTone> = {
  PENDING_WALLET_CHECK:     "neutral",
  WALLET_FUNDS_BLOCKED:     "warning",
  AGENT_OFFER_PENDING:      "warning",
  AGENT_ASSIGNED:           "warning",
  AGENT_EN_ROUTE:           "warning",
  IN_PROGRESS:              "warning",
  AWAITING_CLIENT_APPROVAL: "warning",
  APPROVAL_EXPIRED:         "danger",
  DELIVERING:               "warning",
  DELIVERED:                "success",
  PAYMENT_SETTLED:          "success",
  CANCELLED:                "danger",
  DISPUTED:                 "danger",
  REFUNDED:                 "danger",
};

export function tripStatusLabel(status: TripStatus | undefined | null): string {
  if (!status) return "—";
  return TRIP_LABELS[status] ?? status;
}

export function tripStatusTone(status: TripStatus | undefined | null): StatusTone {
  if (!status) return "neutral";
  return TRIP_TONES[status] ?? "neutral";
}

// ─── Statut d'un arrêt (TripStop) ────────────────────────────────────────────

const STOP_LABELS: Record<TripStopStatus, string> = {
  PENDING:                  "À venir",
  TRAVELING:                "En route",
  AT_STOP:                  "Sur place",
  ITEMS_VERIFIED:           "Articles vérifiés",
  AWAITING_CLIENT_APPROVAL: "Validation requise",
  PURCHASING:               "Achats en cours",
  COMPLETED:                "Terminé",
  DISPUTED:                 "En litige",
  SKIPPED:                  "Ignoré",
};

const STOP_TONES: Record<TripStopStatus, StatusTone> = {
  PENDING:                  "neutral",
  TRAVELING:                "warning",
  AT_STOP:                  "warning",
  ITEMS_VERIFIED:           "warning",
  AWAITING_CLIENT_APPROVAL: "warning",
  PURCHASING:               "warning",
  COMPLETED:                "success",
  DISPUTED:                 "danger",
  SKIPPED:                  "neutral",
};

export function stopStatusLabel(status: TripStopStatus | undefined | null): string {
  if (!status) return "—";
  return STOP_LABELS[status] ?? status;
}

export function stopStatusTone(status: TripStopStatus | undefined | null): StatusTone {
  if (!status) return "neutral";
  return STOP_TONES[status] ?? "neutral";
}

// ─── Origine d'une annulation ────────────────────────────────────────────────

/**
 * Qui a mis fin à la course — contrat du 2026-08-28.
 *
 * ⚠️ TROIS valeurs, pas deux. Outre le client et un administrateur, une tâche
 * automatique peut trancher : délai de validation dépassé, aucune Main trouvée,
 * plafond de sollicitations atteint. C'est le cas le moins visible en
 * développement et le plus fréquent aux heures creuses — un libellé qui n'en
 * prévoit que deux affiche alors un vide, ou pire, accuse le client.
 *
 * Une valeur inconnue retombe sur une formulation neutre plutôt que d'exposer
 * un identifiant brut : le contrat peut en ajouter une sans prévenir.
 */
export function cancelledByLabel(by: string | null | undefined): string {
  switch (by) {
    case "client":
      return "Annulée par le client";
    case "admin":
      return "Annulée par le support";
    case "systeme":
      return "Annulée automatiquement";
    default:
      return "Annulée";
  }
}

// ─── Catégorisation (filtrage UI) ────────────────────────────────────────────

const TRIP_ACTIVE = new Set<TripStatus>([
  "PENDING_WALLET_CHECK",
  "WALLET_FUNDS_BLOCKED",
  "AGENT_OFFER_PENDING",
  "AGENT_ASSIGNED",
  "AGENT_EN_ROUTE",
  "IN_PROGRESS",
  // En attente de validation client : la course est toujours en cours, elle
  // attend simplement une action de l'utilisateur (sinon elle expire).
  "AWAITING_CLIENT_APPROVAL",
  "DELIVERING",
]);

const TRIP_FINAL = new Set<TripStatus>([
  "DELIVERED",
  "PAYMENT_SETTLED",
]);

const TRIP_FAILED = new Set<TripStatus>([
  "CANCELLED",
  "DISPUTED",
  "REFUNDED",
  // Le client n'a pas validé dans le délai → course annulée (cancellation_fee).
  "APPROVAL_EXPIRED",
]);

export function isTripActive(status: TripStatus): boolean {
  return TRIP_ACTIVE.has(status);
}

export function isTripFinal(status: TripStatus): boolean {
  return TRIP_FINAL.has(status);
}

export function isTripFailed(status: TripStatus): boolean {
  return TRIP_FAILED.has(status);
}

// La course attend une décision du client (valider/ajuster les articles vérifiés
// par l'agent). C'est le signal pour afficher l'écran/bandeau d'approbation.
export function isTripAwaitingApproval(status: TripStatus | undefined | null): boolean {
  return status === "AWAITING_CLIENT_APPROVAL";
}

// L'arrêt est "terminé" (n'attend plus d'action agent) s'il est COMPLETED ou SKIPPED.
export function isStopDone(status: TripStopStatus | undefined | null): boolean {
  return status === "COMPLETED" || status === "SKIPPED";
}

// L'arrêt attend la validation du client sur ses articles.
export function isStopAwaitingApproval(status: TripStopStatus | undefined | null): boolean {
  return status === "AWAITING_CLIENT_APPROVAL";
}
